import { SubscribeState } from "@kase/my-marathon-communication";
import { ReadonlySignal, Signal, computed, signal } from "@preact/signals";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import {
    DiscoveryDetailedRevision,
    DiscoveryInfo,
    SetActiveTagDetailsRequest,
    SetTagSummaryRequest,
    UpdateTagDetailsRequest,
} from "./interfaces";

const DEFAULT_LANG = "EN";

export const makeDiscovery = ({ connection }: ApiSliceParams) => {
    const items = signal<DiscoveryInfo[]>([]);
    const loadingStore: Record<string, Signal<boolean>> = {};
    const loadedStore: Record<string, Signal<boolean>> = {};
    const infoStore: Record<string, ReadonlySignal<DiscoveryInfo | null>> = {};
    const historyStore: Record<
        string,
        Signal<DiscoveryDetailedRevision[]>
    > = {};
    const historyLoadedStore: Record<string, Signal<boolean>> = {};
    const historyLoadingStore: Record<string, Signal<boolean>> = {};

    const loadByTags = async (...tags: string[]) => {
        const con = connection.value;
        const lang = DEFAULT_LANG;

        for (const tag of tags) {
            loadingStore[tag].value = true;
        }

        for await (const event of con.discovery.list(lang, ...tags)) {
            if (event.kind === SubscribeState.Remove) {
                items.value = items
                    .peek()
                    .filter((item) =>
                        event.values.every((value) => value.tag !== item.tag),
                    );
            } else {
                items.value = upsert(items.peek().slice(), event.values, "tag");
            }
        }

        for (const tag of tags) {
            loadedStore[tag].value = true;
            loadingStore[tag].value = false;
        }
    };

    const loading = (tag: string) => {
        if (!(tag in loadingStore)) {
            loadingStore[tag] = signal(false);
            loadedStore[tag] = signal(false);
        }

        return loadingStore[tag];
    };

    const loaded = (tag: string) => {
        if (!(tag in loadedStore)) {
            loadingStore[tag] = signal(false);
            loadedStore[tag] = signal(false);
        }

        return loadedStore[tag];
    };

    const getByTag = (tag: string) => {
        if (!(tag in loadingStore)) {
            loadingStore[tag] = signal(false);
            loadedStore[tag] = signal(false);
        }

        if (!(tag in infoStore)) {
            infoStore[tag] = computed(
                () => items.value.find((item) => item.tag === tag) ?? null,
            );
            loadByTags(tag);
        }

        return infoStore[tag];
    };

    const defer = (tag: string, load: boolean) => {
        if (!load) {
            return null;
        }

        getByTag(tag);

        return loadedStore[tag];
    };

    const loadHistory = async (tag: string) => {
        const con = connection.value;
        historyLoadingStore[tag].value = true;

        const edit = await con.discovery.edit;
        const item = items.peek().find((item) => item.tag === tag);

        if (!item) {
            historyLoadingStore[tag].value = false;
            historyLoadedStore[tag].value = true;
            return null;
        }

        const result = await edit.history(item.id);
        const history = result.history.map((v, idx) => ({
            ...v,
            detailedId: Number(v.detailedId),
            localId: idx + 1,
        }));

        historyStore[tag].value = upsert(
            historyStore[tag].peek().slice(),
            history,
            "detailedId",
        );
        historyLoadedStore[tag].value = true;
        historyLoadingStore[tag].value = false;
    };

    const getHistory = (tag: string) => {
        if (!(tag in historyLoadedStore)) {
            historyLoadedStore[tag] = signal(false);
            historyLoadingStore[tag] = signal(false);
        }

        if (!(tag in historyStore)) {
            historyStore[tag] = signal([]);
            loadHistory(tag);
        }

        return historyStore[tag];
    };

    const applyDiff = async (
        history: DiscoveryDetailedRevision[],
        detailedId: number,
    ) => {
        const con = connection.value;
        const edit = await con.discovery.edit;

        return edit.createDiffManager(history).render(detailedId);
    };

    const historyLoaded = (tag: string) => {
        if (!(tag in historyLoadedStore)) {
            historyLoadedStore[tag] = signal(false);
            historyLoadingStore[tag] = signal(false);
        }

        return historyLoadedStore[tag];
    };

    const historyLoading = (tag: string) => {
        if (!(tag in historyLoadingStore)) {
            historyLoadedStore[tag] = signal(false);
            historyLoadingStore[tag] = signal(false);
        }

        return historyLoadingStore[tag];
    };

    const updateTagDetails = async ({
        tag,
        details,
        lang,
        detailedId: parent,
        activate = false,
    }: UpdateTagDetailsRequest) => {
        const con = connection.value;
        const edit = await con.discovery.edit;
        const item = items.peek().find((item) => item.tag === tag);
        let newDetailedId = 1;

        if (item) {
            const history = await edit.history(item.id);
            // if parent is zero, active revision should be parent
            const detailedId =
                (parent ||
                    history.history.find((value) => value.active)
                        ?.detailedId) ??
                1;

            const result = await edit.update({
                id: item.id,
                content: {
                    full: details,
                },
                detailedId,
            });

            if (activate) {
                await edit.activate(item.id, result.detailed.detailedId);
            }

            newDetailedId = result.detailed.detailedId;
        } else {
            await edit.create({
                tag,
                lang,
                detailed: details,
            });
        }

        await loadByTags(tag);
        await loadHistory(tag);

        if (!item) {
            newDetailedId =
                historyStore[tag]?.peek().find((i) => i.active)?.detailedId ??
                newDetailedId;
        }

        return newDetailedId;
    };

    const setActiveTagDetails = async ({
        tag,
        detailedId,
    }: SetActiveTagDetailsRequest) => {
        const con = connection.value;
        const edit = await con.discovery.edit;
        const item = items.peek().find((item) => item.tag === tag);

        if (!item) {
            throw new Error(`Items with tag: '${tag}' is not found`);
        }

        const tagId = item.id;

        await edit.activate(tagId, detailedId);

        await loadByTags(tag);
        await loadHistory(tag);

        return detailedId;
    };

    const setTagSummary = async ({
        tag,
        selector,
        summary,
        lang,
    }: SetTagSummaryRequest) => {
        const con = connection.value;
        const edit = await con.discovery.edit;
        const item = items.peek().find((item) => item.tag === tag);

        let tagId: number | undefined = item?.id;

        if (!tagId) {
            const newItem = await edit.create({
                tag,
                lang,
                detailed: "",
            });

            tagId = newItem.id;
        }

        await edit.setSummary({
            tagId,
            selector,
            summary,
        });

        await loadByTags(tag);
    };

    return {
        items,
        historyStore,
        loading,
        loaded,
        getByTag,
        getHistory,
        historyLoading,
        historyLoaded,
        defer,
        updateTagDetails,
        setActiveTagDetails,
        setTagSummary,
        applyDiff,
    };
};
